import React, { use, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../hooks/useSelector";
import CertificateOverview from "../components/certificateOverview";
import Charts from "../components/charts";
import IssuedCertificates from "../components/issuedCertificates";
import PendingRequests from "../components/pendingRequests";
import { ROLES, VIEWER_ROLES } from "../constants/textconstants";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { setCurrentUser, setAccessRoles, showError } from "../store/slices/formconfig";
import { get } from "../config/api";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { Loader } from "../components/loader";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TESTING_DATA } from "../constants/isTesting";
import { usePermissions } from "../hooks/usePermissions";
import { FEATURES } from "../config/permissions";
import {
  isUnitLevelUser,
  isCountryLevelUser,
} from "../helper-functions";


const OverviewPage: React.FC = () => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();

  let userData = useSelector((state: any) => state.formConfig.user);
  const [partnerSummary, setPartnerSummary] = useState<any>([]);
  const [requestSummary, setRequestSummary] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [overviewData, setOverviewData] = useState<any>(null); // State to store the fetched overview data
  const [barchartData, setBarchartdata] = useState<any>(null);
  const [pendingDeliverySummary, setPendingDeliverySummary] = useState<any[]>([]);

  //start dummy for testering
  const [searchParams] = useSearchParams();
  //end dummy for testing

  const { data: requests, issuedCertificates: allIssuedCertificates } =
    useSelector((state: any) => state.requests || {});
  const issuedCertificates = allIssuedCertificates.filter((cert: any) =>
    ["active", "cancelled", "rejected", "expired"].includes(
      cert.status.toLowerCase()
    )
  );

  const fetchInitialData = async (cwid: string) => {
    setIsLoading(true);

    try {
      const overviewData = await get(`/users/${cwid}/certificates/overview`);
      // const overviewData = DUMMY_OVERVIEWDATA;

      const fetchedOverviewData = overviewData;
      setOverviewData(fetchedOverviewData);

      if (fetchedOverviewData) {

        setBarchartdata(fetchedOverviewData.location_wise_summary);


        const { retailer_wise_summary, distributor_wise_summary } =
          fetchedOverviewData;
        setPartnerSummary([
          {
            total: retailer_wise_summary.total,
            userType: "retailer",
            data: [
              {
                name: "REQUESTED FOR PC",
                value: retailer_wise_summary.issued,
                color: "#ff7f63",
              },
              {
                name: "NOT REQUESTED FOR PC",
                value: retailer_wise_summary.not_issued,
                color: "#009688",
              },
            ],
          },
          {
            total: distributor_wise_summary.total,
            userType: "distributor",
            data: [
              {
                name: "REQUESTED FOR PC",
                value: distributor_wise_summary.issued,
                color: "#ff7f63",
              },
              {
                name: "NOT REQUESTED FOR PC",
                value: distributor_wise_summary.not_issued,
                color: "#009688",
              },
            ],
          },
        ]);

        // Request Summary
        setRequestSummary(
          fetchedOverviewData.request_summary.map((item: any) => ({
            name: item.status.toUpperCase(),
            value: item.count,
            color: `var(--status-${item.status
              .toLowerCase()
              .replace(" ", "-")})`,
          }))
        );

        // Pending Delivery Summary
        if (fetchedOverviewData.pending_delivery_summary) {
          const pds = fetchedOverviewData.pending_delivery_summary;
          setPendingDeliverySummary([
            { name: 'NOT RECEIVED', value: pds.not_received, color: '#4D8EB3' },
            { name: 'NO RESPONSE', value: pds.total - pds.not_received, color: '#ff7f63' },
          ]);
        } else {
          setPendingDeliverySummary([]);
        }
      }
    } catch (error) {
      dispatch(showError({ message: `Failed to fetch overview data, due to some technical issue, please try again`, visible: true }))

      console.error("Failed to fetch overview data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get the logged-in user's account only if authenticated
  const user = isAuthenticated && accounts?.length > 0 ? accounts?.[0] : null;

  const FetchAndSetUserINFO = async (cwid: any) => {
    try {
      let apiResponse: any = await get(
        `${API_ENDPOINTS.USER_BY_ID(cwid)}`
      );

      if (apiResponse) {
        // Transform new multi-role response to old single-role format
        let currentUser: any;
        if (apiResponse.access && Array.isArray(apiResponse.access) && apiResponse.access.length > 0) {
          const { access, ...baseUser } = apiResponse;

          const selectedAccess = access[0]; // Default to first role
          currentUser = {
            ...baseUser,
            role: selectedAccess.role,
            mappedLocations: selectedAccess.mappedLocations,
          };
          await dispatch(setAccessRoles(access));
        } else {
          // Backward compatibility with old single-role format
          currentUser = apiResponse;
        }

        if (
          currentUser.role &&
          Object.values(ROLES)
            .map((r) => r.toLowerCase())
            .includes(currentUser.role.toLowerCase()) ||  Object.keys(VIEWER_ROLES)
            .map((r) => r.toLowerCase())
            .includes(currentUser.role.toLowerCase())
        ) {
          currentUser.userId = cwid;
          await dispatch(setCurrentUser(currentUser));
          if (currentUser.is_viewed_guided_tour) {
            fetchInitialData(cwid);
          } else {
            setDummyData();
          }

        } else {
          // if(currentUser.role.toLowerCase()==='gbs'){
          //   navigate("/review-certificates", { state: { username: currentUser.username } });
          // }else{
             navigate("/unautherized");
          // }
         
        }
      } else {
        navigate("/noaccess");
      }
    } catch (error:any) {
      if (error.response && error.response.status === 400) {
        navigate("/unautherized");
      } else {
        navigate("/noaccess");
      }
      console.error("Failed to fetch user information:", error); // It's good practice to log the error
    }
  };

  useEffect(() => {
    // here is the auth provided user, uncomment this once fix
    if (location.state?.fromGuidedTour) {
      return;
    }
    if (user) {
      const cwid = user?.idTokenClaims?.["https://bayer.com/cwid"];
      if (cwid) {
        if (!userData || userData.userId !== cwid) {
          FetchAndSetUserINFO(cwid);
        }
      }
    } else {
      navigate("/login");
    }
  }, [user, dispatch, location.state, userData]);
  
  const setDummyData = () => {
    // this is the tutorial scenario
    setOverviewData(TESTING_DATA.overViewData);
    const { retailer_wise_summary, distributor_wise_summary } =
      TESTING_DATA.overViewData;
    setPartnerSummary([
      {
        total: retailer_wise_summary.total,
        userType: "retailer",
        data: [
          {
            name: "REQUESTED FOR PC",
            value: retailer_wise_summary.issued,
            color: "#ff7f63",
          },
          {
            name: "NOT REQUESTED FOR PC",
            value: retailer_wise_summary.not_issued,
            color: "#009688",
          },
        ],
      },
      {
        total: distributor_wise_summary.total,
        userType: "distributor",
        data: [
          {
            name: "REQUESTED FOR PC",
            value: distributor_wise_summary.issued,
            color: "#ff7f63",
          },
          {
            name: "NOT REQUESTED FOR PC",
            value: distributor_wise_summary.not_issued,
            color: "#009688",
          },
        ],
      },
    ]);
    // Request Summary
    setRequestSummary(
      TESTING_DATA.overViewData.request_summary.map((item: any) => ({
        name: item.status.toUpperCase(),
        value: item.count,
        color: `var(--status-${item.status
          .toLowerCase()
          .replace(" ", "-")})`,
      }))
    );
    setBarchartdata(TESTING_DATA.overViewData.location_wise_summary);

    // Pending Delivery Summary
    if (TESTING_DATA.overViewData.pending_delivery_summary) {
      const pds = TESTING_DATA.overViewData.pending_delivery_summary;
      setPendingDeliverySummary([
        { name: 'NOT RECEIVED', value: pds.not_received, color: '#4D8EB3' },
        { name: 'NO RESPONSE', value: pds.total - pds.not_received, color: '#ff7f63' },
      ]);
    } else {
      setPendingDeliverySummary([]);
    }
  }


  useEffect(() => {
    if (userData && userData.is_viewed_guided_tour === false) {
      setDummyData();
    } else if (userData && userData.is_viewed_guided_tour === true) {
      fetchInitialData(userData.userId);
    }
  }, [userData])

  // useEffect(() => {
  //   const USERLOGEDIN = searchParams.get("USER");
  //   if (USERLOGEDIN === ROLES.ISSUER) {
  //     FetchAndSetUserINFO("GNZPD");
  //   } else if (USERLOGEDIN === ROLES.APPROVER) {
  //     FetchAndSetUserINFO("GNZPD");
  //   } else {
  //     FetchAndSetUserINFO("GNZPD");
  //   }
  //   // here is the auth provided user, uncomment this once fix
  //   if (user) {
  //     const cwid = 'GNZPD';
  //     if (cwid) {
  //       FetchAndSetUserINFO(cwid);
  //     }
  //   }
  // }, [user, dispatch]);
  return (
    <>
      {Object.keys(userData).length > 0 ? (
        <div style={{ background: "var(--color-light-bg)" }}>
          {isLoading && <Loader />}
          <div style={{ paddingTop: 60 }}>
            {overviewData && (
              <CertificateOverview
                totalRequests={overviewData.total_certificates_issued}
                activeCertificates={overviewData.total_active_certificates}
                pendingRequests={0}
                expiredCertificates={overviewData.total_expired_certificates}
                cancelledCertificates={overviewData.total_cancelled_certificates}
                userId={userData.userId}
              />
            )}
            {partnerSummary.length > 0 && (
              <div
                className="chartsContainer"
                style={{
                  padding: "var(--padding-card)",
                  paddingLeft: "var(--padding-page)",
                  paddingRight: "var(--padding-page)",
                  paddingBottom: 0,
                }}
              >
                <Charts
                  type="pie"
                  data={partnerSummary}
                  multiple={true}
                  heading={"Partner Summary"}
                  total={overviewData.total_certificate_requests}
                />
                <Charts
                  type="pie"
                  data={requestSummary}
                  multiple={false}
                  heading={"Request Summary"}
                  total={overviewData.total_certificate_requests}
                />
                {pendingDeliverySummary.length > 0 && (
                  <Charts
                    type="pie"
                    data={pendingDeliverySummary}
                    multiple={false}
                    heading={"Pending Delivery Summary"}
                    total={overviewData.pending_delivery_summary?.total}
                  />
                )}
              </div>
            )}

            {Array.isArray(barchartData) &&
              barchartData.length > 0 &&
              can(FEATURES.HOME_BAR_CHART) && (
                <div
                  className="chartsContainer"
                  style={{
                    padding: "var(--padding-card)",
                    paddingLeft: "var(--padding-page)",
                    paddingRight: "var(--padding-page)",
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  <Charts
                    type="bar"
                    data={barchartData}
                    multiple={false}
                    heading={userData && isCountryLevelUser(userData) ? "Certificates Issued By Units": isUnitLevelUser(userData)? "Certificates Issued By Squad" : "Certificates Issued By Territory"}
                  />
                </div>
              )}

            <div
              style={{
                paddingLeft: "var(--padding-page)",
                paddingRight: "var(--padding-page)",
                paddingBottom: "var(--padding-page)",
              }}
            >
              {can(FEATURES.HOME_PENDING_REQUESTS) && (
                <PendingRequests showHeaderActions={false} />
              )}
              {can(FEATURES.HOME_ISSUED_CERTIFICATES) && 
                <IssuedCertificates
                  showHeaderActions={false}
                />
              }
              
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100%",
            backgroundColor: "var(--color-light-bg)",
          }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};

export default OverviewPage;
